import { Fetcher } from "hooks/fetch/Fetch";
import { Account, AccountDetails, AccountDetailsQuery, AccountQuery } from "types/Customer";

export interface CustomerRepository {
    findBy(query: AccountQuery): Promise<Account[]>
    findDetailsBy(query: AccountDetailsQuery): Promise<AccountDetails | undefined>
}

export class InmemoryCustomerRepository implements CustomerRepository {
    private readonly accounts: Account[];
    private readonly accountDetails: Map<string, AccountDetails>;

    constructor(accounts: Account[], accountDetails?: Map<string, AccountDetails>) {
        this.accounts = accounts;
        this.accountDetails = accountDetails ?? new Map<string, AccountDetails>();
    }
    
    async findDetailsBy(query: AccountDetailsQuery): Promise<AccountDetails | undefined> {
        if (query.salesCompanyId === undefined || query.number === undefined) {
            return undefined;
        }

        return this.accountDetails.get(this.resolveAccountDetailsKey(query.salesCompanyId, query.number));
    }

    async findBy(query: AccountQuery): Promise<Account[]> {
        if (query.searchTerm === undefined || query.searchTerm === '') {
            return [];
        }

        const normalizedSearchTerm = query.searchTerm.toLowerCase();

        return this.accounts.filter(account => {
            if (account.salesCompanyId !== query.salesCompanyId) {
                return false;
            }

            return account.number.toLowerCase().includes(normalizedSearchTerm)
                || account.name.toLowerCase().includes(normalizedSearchTerm)
                || account.siteId.toLowerCase().includes(normalizedSearchTerm);
        });
    }

    private resolveAccountDetailsKey(salesCompanyId: string, number: string): string {
        return `${salesCompanyId}:${number}`;
    }
}

interface HttpCustomerSearchResponse {
    customers: [
        {
            siteId: string
            accountNum: string
            isBlocked: string
            name: string
        }
    ]
}

export class HttpCustomerRepository implements CustomerRepository {
    private readonly baseUrl: string;
    private readonly baseDetailsUrl: string;
    private readonly fetch: Fetcher;

    constructor(baseUrl: string, baseDetailsUrl: string, fetcher: Fetcher) {
        this.baseUrl = baseUrl;
        this.baseDetailsUrl = baseDetailsUrl;
        this.fetch = fetcher;
    }
    async findDetailsBy(query: AccountDetailsQuery): Promise<AccountDetails | undefined> {
        if (query.salesCompanyId === undefined || query.salesCompanyId === '') {
            throw new Error("HttpCustomerRepository.findDetailsBy: salesCompanyId must be set");
        }

        if (query.number === undefined || query.number === '') {
            throw new Error("HttpCustomerRepository.findDetailsBy: account number must be set");
        }

        const response = await this.fetch(`${this.baseDetailsUrl}?ErpDataId=${query.salesCompanyId}&accountNum=${query.number}`, {
            method: 'GET',
        });

        if (response.status === 404) {
            return undefined;
        }

        const json = await response.json();

        return {
            priceGroup: json['priceGroup'] !== null && json['priceGroup'] !== "" ? json['priceGroup']: undefined,
            discountGroup: json['discountGroup']
        };
    }
    
    async findBy(query: AccountQuery): Promise<Account[]> {
        if (query.searchTerm === undefined || query.searchTerm === '') {
            throw new Error("HttpCustomerRepository.findBy: searchTerm must be set");
        }

        const response = await this.fetch(`${this.baseUrl}?erpDataId=${query.salesCompanyId}&query=${query.searchTerm}&limit=100`, {
            method: 'POST',
        });

        if (response.status === 404) {
            return [];
        }

        const json = await response.json();
        const data = {
            customers: json
        } as HttpCustomerSearchResponse;

        return data.customers.map((val) => {
            return {
                salesCompanyId: query.salesCompanyId,
                siteId: val.siteId,
                number: val.accountNum,
                name: val.name,
                isBlocked: val.isBlocked === "0",
            }
        });
    }

}